import { GameObjects, Math as PhaserMath, Scene } from 'phaser';
import type { BalanceArenaQuestionResult, FinishPlayAttemptInput } from '../../../../shared/results';
import {
  BALANCE_GAME_FINISHED_EVENT,
  BALANCE_HUD_UPDATE_EVENT,
  EventBus,
  type BalanceGameResult,
  type BalanceHudUpdate,
} from '../../../game/EventBus';
import { buildLearningReview } from '../../../game/domain/learningReview';
import { createLearningResultOverlay, RESULT_OVERLAY_DEPTH } from '../../../game/ui/LearningResultOverlay';
import type { BalanceArenaChoice, BalanceArenaConfig } from '../domain/balanceLevels';
import { getBalanceResultMessage } from '../domain/balanceResultMessage';
import {
  advanceBalanceMotion,
  applyCorrectBalance,
  createBalanceMotionState,
  FALL_EDGE,
  maybeReverseWind,
  SAFE_CENTER_BAND,
  type BalanceMotionState,
} from '../domain/balanceMotion';
import { BalanceAudio } from './BalanceAudio';
import { BALANCE_ENDLESS, shuffledBalanceRound } from '../../../../shared/balanceRounds';

const WIDTH = 1280;
const HEIGHT = 720;
const PLATFORM_Y = 548;
const PLATFORM_WIDTH = 780;
const PLATFORM_HALF_WIDTH = PLATFORM_WIDTH / 2;
type Phase = 'intro' | 'playing' | 'feedback' | 'finished' | 'fallen';

export class BalanceArenaScene extends Scene {
  private readonly config: BalanceArenaConfig;
  private readonly gameId: string;
  private readonly restartEnabled: boolean;
  private readonly audio = new BalanceAudio();
  private readonly mediaTextureKeys = new Map<string, string>();
  private phase: Phase = 'intro';
  private motion: BalanceMotionState = createBalanceMotionState();
  /** Gösterilen soru sayısı - 1 (turlar boyunca artar). */
  private questionIndex = 0;
  /** Bu turdaki soru sırası (seviye numaraları) ve sıradaki konum. */
  private roundOrder: number[] = [];
  private roundPosition = 0;
  private round = 1;
  private timeLimitReached = false;
  private lastHudSecond = -1;
  private questionElapsedMs = 0;
  private survivalDurationMs = 0;
  private score = 0;
  private correct = 0;
  private wrong = 0;
  private questionResults: BalanceArenaQuestionResult[] = [];
  private resultEmitted = false;
  private character?: GameObjects.Container;
  private platform?: GameObjects.Container;
  private windText?: GameObjects.Text;
  private feedbackText?: GameObjects.Text;
  private promptObjects: GameObjects.GameObject[] = [];
  private optionObjects: GameObjects.GameObject[] = [];

  constructor(config: BalanceArenaConfig, gameId: string, restartEnabled = true) {
    super('BalanceArena');
    this.config = config;
    this.gameId = gameId;
    this.restartEnabled = restartEnabled;
  }

  preload() {
    const urls = new Set<string>();
    this.config.questions.forEach((question) => {
      if (question.promptMediaUrl) urls.add(question.promptMediaUrl);
      question.choices.forEach((choice) => { if (choice.mediaUrl) urls.add(choice.mediaUrl); });
    });
    [...urls].forEach((url, index) => {
      const key = `${this.gameId}-balance-media-${index}`;
      this.mediaTextureKeys.set(url, key);
      if (!this.textures.exists(key)) this.load.image(key, url);
    });
  }

  create() {
    this.phase = 'intro';
    this.questionIndex = 0;
    this.roundOrder = this.config.questions.map((question) => question.id);
    this.roundPosition = 0;
    this.round = 1;
    this.timeLimitReached = false;
    this.lastHudSecond = -1;
    this.questionElapsedMs = 0;
    this.survivalDurationMs = 0;
    this.score = 0;
    this.correct = 0;
    this.wrong = 0;
    this.questionResults = [];
    this.resultEmitted = false;
    this.motion = createBalanceMotionState(this.seed());
    this.drawSkyWorld();
    this.drawPlatform();
    this.drawCharacter();
    this.input.on('pointerdown', () => this.audio.unlock());
    this.installDebugHooks();
    this.showIntro();
    this.refreshHud();
    this.events.once('shutdown', () => this.audio.destroy());
    EventBus.emit('current-scene-ready', this);
  }

  update(_time: number, delta: number) {
    if (this.phase !== 'playing' && this.phase !== 'feedback') return;
    const safeDelta = Math.min(80, delta);
    this.motion = advanceBalanceMotion(this.motion, safeDelta);
    this.survivalDurationMs += safeDelta;
    if (this.phase === 'playing') this.questionElapsedMs += safeDelta;
    this.positionCharacter();
    this.animatePlatform(safeDelta);
    if (Math.abs(this.motion.position) >= FALL_EDGE) { this.fall(); return; }
    if (this.config.repeatUntilFall) {
      if (this.survivalDurationMs >= BALANCE_ENDLESS.maxSurvivalMs) { this.timeLimitReached = true; this.finish(true); return; }
      const second = Math.floor(this.survivalDurationMs / 1000);
      if (second !== this.lastHudSecond) { this.lastHudSecond = second; this.refreshHud(); }
    }
  }

  private get currentQuestion() {
    const level = this.roundOrder[this.roundPosition] ?? 1;
    return this.config.questions.find((question) => question.id === level) ?? this.config.questions[0];
  }

  private seed() {
    return [...this.config.id].reduce((total, character) => total + character.charCodeAt(0), 0);
  }

  private drawSkyWorld() {
    this.cameras.main.setBackgroundColor('#69c7eb');
    const graphics = this.add.graphics();
    graphics.fillGradientStyle(0x5ec4ea, 0x5ec4ea, 0xdff5ff, 0xdff5ff, 1);
    graphics.fillRect(0, 0, WIDTH, HEIGHT);
    graphics.fillStyle(0xffffff, 0.48);
    for (let i = 0; i < 15; i += 1) {
      const x = PhaserMath.Between(-80, WIDTH + 80); const y = PhaserMath.Between(90, 400);
      graphics.fillEllipse(x, y, PhaserMath.Between(100, 220), PhaserMath.Between(34, 76));
    }
    graphics.fillStyle(0x376ea0, 0.44);
    graphics.fillTriangle(0, 520, 210, 285, 440, 520);
    graphics.fillTriangle(250, 520, 550, 230, 820, 520);
    graphics.fillTriangle(700, 520, 1010, 270, 1280, 520);
    for (let i = 0; i < 28; i += 1) {
      const mote = this.add.circle(PhaserMath.Between(0, WIDTH), PhaserMath.Between(80, 520), PhaserMath.Between(2, 6), 0xffe783, 0.5);
      this.tweens.add({ targets: mote, y: mote.y - PhaserMath.Between(25, 70), alpha: 0.1, yoyo: true, repeat: -1, duration: PhaserMath.Between(1800, 3600) });
    }
  }

  private drawPlatform() {
    const host = this.add.container(WIDTH / 2, PLATFORM_Y);
    const shadow = this.add.ellipse(0, 56, PLATFORM_WIDTH + 100, 88, 0x143b56, 0.3);
    const underside = this.add.triangle(
      0,
      72,
      -PLATFORM_HALF_WIDTH,
      -42,
      PLATFORM_HALF_WIDTH,
      -42,
      0,
      138,
      0x5f7088,
      1,
    ).setStrokeStyle(5, 0xd7e7ee);
    const top = this.add.ellipse(0, -36, PLATFORM_WIDTH, 126, 0x6dd1c2).setStrokeStyle(8, 0xf6e19d);
    const inner = this.add.ellipse(0, -36, PLATFORM_WIDTH - 90, 84, 0x236e75, 0.78);
    const rings = this.add.ellipse(0, -36, PLATFORM_WIDTH - 240, 58, 0x2e8d87, 0.72).setStrokeStyle(3, 0x9af3df, 0.8);
    host.add([shadow, underside, top, inner, rings]);
    this.platform = host;
    this.windText = this.add.text(WIDTH / 2, 432, '', {
      fontFamily: 'system-ui, sans-serif', fontSize: '22px', fontStyle: 'bold', color: '#133845',
      backgroundColor: '#f7e7a8dd', padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(20);
    this.refreshWindText();
  }

  private drawCharacter() {
    const host = this.add.container(WIDTH / 2, PLATFORM_Y - 98).setDepth(12);
    const glow = this.add.circle(0, 20, 48, 0xffdc65, 0.28);
    const body = this.add.circle(0, 0, 39, 0xffa82f).setStrokeStyle(5, 0x713c20);
    const head = this.add.circle(0, -48, 31, 0xffc667).setStrokeStyle(5, 0x713c20);
    const eyeLeft = this.add.circle(-11, -52, 5, 0x172b3a); const eyeRight = this.add.circle(11, -52, 5, 0x172b3a);
    const armLeft = this.add.rectangle(-43, 0, 12, 48, 0xffa82f).setRotation(0.25);
    const armRight = this.add.rectangle(43, 0, 12, 48, 0xffa82f).setRotation(-0.25);
    const legLeft = this.add.rectangle(-16, 42, 15, 40, 0x5c3aa7); const legRight = this.add.rectangle(16, 42, 15, 40, 0x5c3aa7);
    host.add([glow, armLeft, armRight, legLeft, legRight, body, head, eyeLeft, eyeRight]);
    this.tweens.add({ targets: [armLeft, armRight], angle: '+=10', yoyo: true, repeat: -1, duration: 520 });
    this.tweens.add({ targets: glow, scale: 1.2, alpha: 0.12, yoyo: true, repeat: -1, duration: 850 });
    this.character = host;
  }

  private positionCharacter() {
    if (!this.character) return;
    const x = WIDTH / 2 + this.motion.position * PLATFORM_HALF_WIDTH;
    this.character.x = x;
    this.character.y = PLATFORM_Y - 98 + Math.sin(this.survivalDurationMs / 145) * 3;
    this.character.rotation = PhaserMath.Clamp(this.motion.velocity * 1.8, -0.22, 0.22);
  }

  private animatePlatform(delta: number) {
    if (!this.platform) return;
    this.platform.rotation += this.motion.wind * delta * 0.000006;
  }

  private showIntro() {
    const panel = this.add.rectangle(WIDTH / 2, 270, 750, 260, 0x0b3342, 0.94).setStrokeStyle(4, 0xffd768).setDepth(100);
    const title = this.add.text(WIDTH / 2, 205, 'DENGE ARENASI', { fontFamily: 'system-ui', fontSize: '42px', fontStyle: 'bold', color: '#ffe078' }).setOrigin(0.5).setDepth(101);
    const copy = this.add.text(WIDTH / 2, 282, this.config.repeatUntilFall
      ? 'Sorular bitince karışık sırayla yeniden gelir.\nDüşmeden en uzun süre dayan! (En fazla 5 dakika)'
      : 'Soruları cevapla, rüzgâra karşı dengede kal.\nTek düşüş oyunu bitirir!', { fontFamily: 'system-ui', fontSize: '24px', color: '#e8fbff', align: 'center' }).setOrigin(0.5).setDepth(101);
    const button = this.add.rectangle(WIDTH / 2, 360, 260, 62, 0xf4ca5d).setStrokeStyle(3, 0xffefaf).setInteractive({ useHandCursor: true }).setDepth(101);
    const label = this.add.text(WIDTH / 2, 360, 'ARENEYE ÇIK', { fontFamily: 'system-ui', fontSize: '22px', fontStyle: 'bold', color: '#12333a' }).setOrigin(0.5).setDepth(102);
    button.on('pointerdown', () => { this.audio.play('start'); [panel, title, copy, button, label].forEach((object) => object.destroy()); this.phase = 'playing'; this.showQuestion(); this.refreshHud(); });
  }

  private showQuestion() {
    this.clearQuestion();
    const question = this.currentQuestion;
    this.questionElapsedMs = 0;
    const panel = this.add.rectangle(WIDTH / 2, 105, 1030, 136, 0x0b3043, 0.94).setStrokeStyle(3, 0x74e5ed).setDepth(30);
    this.promptObjects.push(panel);
    let textX = WIDTH / 2; let textWidth = 900;
    if (question.promptMediaUrl) {
      const key = this.mediaTextureKeys.get(question.promptMediaUrl);
      if (key && this.textures.exists(key)) {
        const image = this.add.image(205, 105, key).setDepth(31);
        image.setScale(Math.min(150 / Math.max(1, image.width), 104 / Math.max(1, image.height)));
        this.promptObjects.push(image); textX = 700; textWidth = 760;
      }
    }
    const prompt = this.add.text(textX, 105, question.prompt.trim() || 'Görsel soru', {
      fontFamily: 'system-ui, sans-serif', fontSize: '30px', fontStyle: 'bold', color: '#ffffff', align: 'center',
      wordWrap: { width: textWidth, useAdvancedWrap: true },
    }).setOrigin(0.5).setMaxLines(4).setDepth(31);
    this.promptObjects.push(prompt);
    const count = question.choices.length;
    const width = Math.min(250, (1120 - (count - 1) * 16) / count);
    const total = count * width + (count - 1) * 16;
    question.choices.forEach((choice, index) => this.createChoice(choice, index, WIDTH / 2 - total / 2 + width / 2 + index * (width + 16), 250, width));
  }

  private createChoice(choice: BalanceArenaChoice, index: number, x: number, y: number, width: number) {
    const host = this.add.container(x, y).setDepth(32).setSize(width, 116);
    const card = this.add.rectangle(0, 0, width, 116, 0x164d64, 0.97).setStrokeStyle(3, 0x69e4eb);
    host.add(card);
    let labelY = 0;
    if (choice.mediaUrl) {
      const key = this.mediaTextureKeys.get(choice.mediaUrl);
      if (key && this.textures.exists(key)) {
        const image = this.add.image(0, choice.label.trim() ? -22 : 0, key);
        image.setScale(Math.min((width - 28) / Math.max(1, image.width), (choice.label.trim() ? 58 : 90) / Math.max(1, image.height)));
        host.add(image); labelY = 35;
      }
    }
    if (choice.label.trim()) host.add(this.add.text(0, labelY, `${index + 1}. ${choice.label}`, {
      fontFamily: 'system-ui', fontSize: `${width < 190 ? 16 : 19}px`, fontStyle: 'bold', color: '#f3fcff', align: 'center',
      wordWrap: { width: width - 20, useAdvancedWrap: true },
    }).setOrigin(0.5).setMaxLines(3));
    host.setInteractive({ useHandCursor: true }).on('pointerdown', () => this.choose(index));
    this.optionObjects.push(host);
  }

  private choose(index: number) {
    if (this.phase !== 'playing') return;
    this.audio.unlock();
    this.phase = 'feedback';
    const question = this.currentQuestion;
    const correctIndex = question.choices.findIndex((choice) => choice.correct);
    const isCorrect = index === correctIndex;
    // Rüzgâr yönü, doğru cevabın karakteri merkeze toplamasından önceki gerçek
    // konuma göre değerlendirilir. Böylece kenardaki oyuncu ters rüzgârla
    // yapay biçimde kurtarılmaz.
    const wasSafelyCentered = Math.abs(this.motion.position) <= SAFE_CENTER_BAND;
    this.questionResults.push({
      level: question.id, ...(this.config.repeatUntilFall ? { round: this.round } : {}), durationMs: Math.round(this.questionElapsedMs), selectedChoiceIndex: index,
      correctChoiceIndex: correctIndex, correct: isCorrect, questionPrompt: question.prompt || 'Görsel soru',
      selectedAnswer: question.choices[index].label || 'Görsel seçenek',
      correctAnswer: question.choices[correctIndex].label || 'Görsel seçenek',
    });
    if (isCorrect) {
      this.correct += 1; this.score += 100; this.audio.play('correct'); this.motion = applyCorrectBalance(this.motion);
      this.feedback('DOĞRU! DENGE TOPARLANDI', '#9bf1b4');
    } else {
      this.wrong += 1; this.audio.play('wrong');
      this.feedback(`YANLIŞ · DOĞRU CEVAP: ${question.choices[correctIndex].label || 'Görsel seçenek'}`, '#ffaaa0');
    }
    this.refreshHud();
    this.time.delayedCall(1200, () => {
      if (this.phase !== 'feedback') return;
      let newRound = false;
      if (this.roundPosition + 1 >= this.roundOrder.length) {
        if (!this.config.repeatUntilFall) { this.finish(true); return; }
        // Yeni tur: aynı sorular karışık sırayla; rüzgâr bir tık güçlenir.
        this.round += 1; newRound = true;
        this.roundOrder = shuffledBalanceRound(this.config.questions.length, this.roundOrder.at(-1) ?? null, Math.random);
        this.roundPosition = 0;
        this.motion = { ...this.motion, windScale: this.motion.windScale * BALANCE_ENDLESS.windGrowthPerRound };
      } else {
        this.roundPosition += 1;
      }
      this.questionIndex += 1;
      if (isCorrect && wasSafelyCentered) {
        const next = maybeReverseWind(this.motion, this.config.questions.length, this.questionIndex, this.seed());
        if (next.wind !== this.motion.wind) { this.motion = next; this.audio.play('wind'); this.refreshWindText('Rüzgâr yön değiştiriyor'); }
      }
      this.phase = 'playing'; this.showQuestion(); this.refreshWindText(newRound ? `${this.round}. tur · Rüzgâr güçleniyor` : ''); this.refreshHud();
    });
  }

  private feedback(message: string, color: string) {
    this.feedbackText?.destroy();
    this.feedbackText = this.add.text(WIDTH / 2, 365, message, { fontFamily: 'system-ui', fontSize: '28px', fontStyle: 'bold', color, backgroundColor: '#082934e8', padding: { x: 18, y: 10 } }).setOrigin(0.5).setDepth(45);
  }

  private fall() {
    if (this.phase === 'fallen' || this.phase === 'finished') return;
    this.phase = 'fallen'; this.audio.play('fall'); this.refreshHud();
    if (this.character) this.tweens.add({ targets: this.character, y: HEIGHT + 120, angle: this.motion.position > 0 ? 180 : -180, duration: 850, ease: 'Quad.in' });
    this.time.delayedCall(850, () => this.finish(false));
  }

  private finish(completed: boolean) {
    if (this.resultEmitted) return;
    this.phase = completed ? 'finished' : 'fallen';
    if (completed) this.audio.play('finish');
    this.clearQuestion(); this.feedbackText?.destroy();
    const result: FinishPlayAttemptInput = {
      score: this.score, lives: completed ? 1 : 0, correct: this.correct, wrong: this.wrong,
      attempts: this.questionResults.length, completed,
      details: {
        templateKey: 'balance-arena', fellAtQuestion: completed ? null : this.questionIndex + 1,
        survivalDurationMs: Math.round(this.survivalDurationMs), questions: this.questionResults,
        ...(this.config.repeatUntilFall ? { repeatUntilFall: true, timeLimitReached: this.timeLimitReached } : {}),
      },
    };
    const reviewItems = buildLearningReview(this.config.questions.map((question) => ({
      prompt: question.prompt,
      promptMediaUrl: question.promptMediaUrl,
      choices: question.choices.map((choice) => ({ text: choice.label, mediaUrl: choice.mediaUrl, correct: choice.correct })),
    })), this.reviewEntries().map((entry) => ({
      label: `${entry.level}. soru`, questionIndex: entry.level - 1, wrongChoiceIndices: entry.correct ? [] : [entry.selectedChoiceIndex],
      wrongCount: entry.correct ? 0 : 1, completed: true, completionStatus: entry.correct ? undefined : 'answer-shown',
    })));
    const repeat = this.config.repeatUntilFall;
    const survival = `${Math.floor(this.survivalDurationMs / 60000)}:${String(Math.floor(this.survivalDurationMs / 1000) % 60).padStart(2, '0')}`;
    createLearningResultOverlay(this, {
      centerX: WIDTH / 2, centerY: HEIGHT / 2, viewportWidth: WIDTH, viewportHeight: HEIGHT,
      title: repeat && completed ? 'MAKSİMUM SÜREYE ULAŞTIN, HARİKA!' : completed ? 'DENGE ARENASI TAMAMLANDI!' : 'DENGENİ KAYBETTİN!', titleColor: completed ? '#ffe17d' : '#ffaea4', accentColor: completed ? 0xffd65c : 0xff796f,
      summaryLines: repeat
        ? [`Dayanma süresi: ${survival} · ${this.round}. tur`, `${this.score} puan · ${this.correct} doğru · ${this.wrong} yanlış`]
        : [`${this.score} puan · ${this.correct}/${this.config.questions.length} doğru`, `${this.wrong} yanlış · ${this.questionResults.length}/${this.config.questions.length} soru`, `Dayanma süresi: ${(this.survivalDurationMs / 1000).toFixed(1)} sn`],
      reviewItems,
      emptyReviewMessage: repeat ? (completed ? 'Beş dakika boyunca düşmedin ve hiç yanlış yapmadın!' : 'Düştün ama verdiğin bütün cevaplar doğruydu. Tekrar dene, daha uzun dayan!') : getBalanceResultMessage({
        completed,
        answeredCount: this.questionResults.length,
        wrongCount: this.wrong,
      }),
      restartEnabled: this.restartEnabled, restartLabel: 'Yeniden oyna', onRestart: () => this.scene.restart(),
      resolveTextureKey: (url) => this.mediaTextureKeys.get(url),
    }).setDepth(RESULT_OVERLAY_DEPTH);
    if (!this.resultEmitted) { this.resultEmitted = true; EventBus.emit(BALANCE_GAME_FINISHED_EVENT, { gameId: this.gameId, result } satisfies BalanceGameResult); }
    this.refreshHud();
  }

  /** Öğrenme özeti: tekrar modunda her yanlış soru bir kez listelenir. */
  private reviewEntries() {
    if (!this.config.repeatUntilFall) return this.questionResults;
    const seen = new Set<number>();
    return this.questionResults.filter((entry) => !entry.correct && !seen.has(entry.level) && seen.add(entry.level));
  }

  private clearQuestion() { this.promptObjects.forEach((item) => item.destroy()); this.optionObjects.forEach((item) => item.destroy()); this.promptObjects = []; this.optionObjects = []; }
  private refreshWindText(prefix = '') { if (this.windText) this.windText.setText(`${prefix ? `${prefix} · ` : ''}Rüzgâr ${this.motion.wind > 0 ? 'sağa →' : '← sola'}`); }
  private refreshHud() {
    EventBus.emit(BALANCE_HUD_UPDATE_EVENT, { gameId: this.gameId, hud: {
      question: Math.min(this.roundPosition + 1, this.config.questions.length), totalQuestions: this.config.questions.length, phase: this.phase, score: this.score, wind: this.motion.wind,
      ...(this.config.repeatUntilFall ? { repeat: { round: this.round, answered: this.questionResults.length, survivalMs: Math.round(this.survivalDurationMs) } } : {}),
    } } satisfies BalanceHudUpdate);
  }
  private installDebugHooks() {
    window.render_game_to_text = () => JSON.stringify({
      template: 'balance-arena', phase: this.phase, question: this.questionIndex + 1, level: this.currentQuestion.id, position: Number(this.motion.position.toFixed(3)),
      wind: this.motion.wind, windScale: Number(this.motion.windScale.toFixed(4)), score: this.score, correct: this.correct, wrong: this.wrong,
      repeatUntilFall: this.config.repeatUntilFall, round: this.round, answered: this.questionResults.length, survivalMs: Math.round(this.survivalDurationMs),
      options: this.currentQuestion.choices.map((choice, index) => ({ index: index + 1, correct: choice.correct })),
    });
    window.advanceTime = (milliseconds: number) => { const steps = Math.ceil(milliseconds / 16); for (let index = 0; index < steps; index += 1) this.update(0, Math.min(16, milliseconds - index * 16)); };
  }
}
